import React, { useState, useEffect, useCallback } from 'react';
import {
  Modal,
  Button,
  Form,
  ListGroup,
  InputGroup,
  Alert,
  Collapse,
} from 'react-bootstrap';
import {
  ChevronUp,
  ChevronDown,
  ChevronRight,
  Trash3,
  Plus,
  Palette,
  Save,
  GripVertical,
  ArrowCounterclockwise,
} from 'react-bootstrap-icons';
import type { Order } from '../../../models/order';
import type {
  DeliveryNoteBlockId,
  DeliveryNoteLayoutConfig,
  DeliveryNoteBlockAlignment,
  DeliveryNoteTypography,
  ConfigurationFactors,
} from '../../../models/configuration';
import {
  DEFAULT_DELIVERY_NOTE_LAYOUT,
  DEFAULT_DELIVERY_NOTE_FONT_FAMILY,
  DEFAULT_DELIVERY_NOTE_TEXT_COLOR,
  DEFAULT_DELIVERY_NOTE_BASE_FONT_SIZE_PX,
  resolveDeliveryNoteShowLineDiscountColumn,
} from '../../../models/configuration';
import {
  loadConfigurationFactors,
  updateConfigurationFactors,
} from '../../../api/configuration';
import { buildDeliveryNoteHtml } from './buildDeliveryNoteHtml';
import type { BrandingFactors } from '../../../models/configuration';
import type { VesFiscalData } from '../../../models/organization';
import { formatExchangeRate } from '../../../utils/formatExchangeRate';

const BLOCK_LABELS: Record<DeliveryNoteBlockId, string> = {
  title: 'Título (Nota de Entrega)',
  logo: 'Logo',
  sale_number: 'Número de venta',
  client: 'Cliente',
  salesperson: 'Vendedor',
  date: 'Fecha',
  delivery_address: 'Dirección de entrega',
  observations: 'Observaciones',
  products_table: 'Tabla de productos',
  totals: 'Totales',
  totals_breakdown: 'Desglose (exento, base, IVA, total)',
  exchange_hint: 'Tasa de cambio (pie)',
  fiscal_rif: 'R.I.F. (fiscal)',
  fiscal_taxpayer_type: 'Tipo contribuyente',
  fiscal_withholding_agent: 'Agente retención',
  fiscal_address: 'Dirección fiscal',
  fiscal_state: 'Estado (fiscal)',
  fiscal_municipality: 'Municipio (fiscal)',
};

const ALL_BLOCK_IDS: DeliveryNoteBlockId[] = [
  'title',
  'logo',
  'sale_number',
  'client',
  'salesperson',
  'date',
  'delivery_address',
  'observations',
  'products_table',
  'totals',
  'totals_breakdown',
  'exchange_hint',
  'fiscal_rif',
  'fiscal_taxpayer_type',
  'fiscal_withholding_agent',
  'fiscal_address',
  'fiscal_state',
  'fiscal_municipality',
];

const VALID_BLOCK_IDS_SET = new Set(ALL_BLOCK_IDS);

function safeBlockOrder(order: unknown): DeliveryNoteBlockId[] {
  if (!Array.isArray(order))
    return [...DEFAULT_DELIVERY_NOTE_LAYOUT.blockOrder];
  return order.filter((id): id is DeliveryNoteBlockId =>
    VALID_BLOCK_IDS_SET.has(id as DeliveryNoteBlockId)
  );
}

function safeRecord<K extends string, V>(
  val: unknown,
  defaultVal: Record<K, V>
): Record<K, V> {
  if (val == null || typeof val !== 'object' || Array.isArray(val))
    return defaultVal;
  return { ...(val as Record<string, V>) } as Record<K, V>;
}

const ALIGN_OPTIONS: { value: DeliveryNoteBlockAlignment; label: string }[] = [
  { value: 'left', label: 'Izq' },
  { value: 'center', label: 'Centro' },
  { value: 'right', label: 'Der' },
];

const FONT_OPTIONS: { value: string; label: string }[] = [
  { value: 'Arial, Helvetica, sans-serif', label: 'Arial' },
  { value: 'Georgia, serif', label: 'Georgia' },
  { value: 'Times New Roman, Times, serif', label: 'Times New Roman' },
  { value: 'Inter, system-ui, -apple-system, sans-serif', label: 'Inter' },
  {
    value: 'system-ui, -apple-system, Segoe UI, Roboto, sans-serif',
    label: 'System',
  },
  { value: 'Verdana, Geneva, sans-serif', label: 'Verdana' },
  { value: 'Courier New, Courier, monospace', label: 'Courier New' },
  { value: 'Trebuchet MS, sans-serif', label: 'Trebuchet MS' },
];
const FONT_OPTIONS_VALUES = new Set(FONT_OPTIONS.map(o => o.value));
const CUSTOM_FONT_VALUE = '__custom__';

/** Sample order for preview */
function getSampleOrder(): Order {
  const now = new Date();
  return {
    id: 'sample-id-123',
    clientId: 'client-1',
    clientName: 'Cliente Ejemplo C.A.',
    deliveryAddress: 'Av. Principal, Edif. Torre, Piso 5, Oficina 501',
    creatorSalesPerson: 'María Pérez',
    paymentMethod: 'Transferencia',
    subtotal: 1500,
    ivaAmount: 240,
    total: 1740,
    exchangeRate: 36,
    products: [
      {
        productId: 'p1',
        code: 'ART-001',
        name: 'Producto ejemplo A',
        price: 100,
        quantity: 5,
        discount: 10,
      },
      {
        productId: 'p2',
        code: 'ART-002',
        name: 'Producto ejemplo B',
        price: 50,
        quantity: 20,
        discount: 0,
      },
    ],
    approvalState: 'approved',
    saleNumber: 1001,
    comment: 'Entregar en horario de oficina.',
    created: now,
    updated: now,
  } as Order;
}

interface DeliveryNoteSandboxProps {
  show: boolean;
  onClose: () => void;
  organizationId: string;
  organizationName: string;
  logoUrl: string;
  branding: BrandingFactors | null;
  referenceCurrency: string;
  organizationCurrency: string;
  formatMoney: (value: number, code: string) => string;
  addToast: (message: string) => void;
  onSaved?: () => void;
  /** Organization fiscal data (VES) – enables fiscal_* blocks in preview/print */
  fiscalData?: VesFiscalData | null;
}

export default function DeliveryNoteSandbox({
  show,
  onClose,
  organizationId,
  organizationName,
  logoUrl,
  branding,
  referenceCurrency,
  organizationCurrency,
  formatMoney,
  addToast,
  onSaved,
  fiscalData,
}: DeliveryNoteSandboxProps) {
  const [blockOrder, setBlockOrder] = useState<DeliveryNoteBlockId[]>(
    DEFAULT_DELIVERY_NOTE_LAYOUT.blockOrder
  );
  const [blockAlignments, setBlockAlignments] = useState<
    Partial<Record<DeliveryNoteBlockId, DeliveryNoteBlockAlignment>>
  >({});
  const [typography, setTypography] = useState<DeliveryNoteTypography>({});
  const [blockFontSizes, setBlockFontSizes] = useState<
    Partial<Record<DeliveryNoteBlockId, number>>
  >({});
  const [blockColors, setBlockColors] = useState<
    Partial<Record<DeliveryNoteBlockId, string>>
  >({});
  const [titleIncludesNumber, setTitleIncludesNumber] = useState(true);
  const [showAmounts, setShowAmounts] = useState(
    DEFAULT_DELIVERY_NOTE_LAYOUT.defaultShowAmounts ?? false
  );
  const [amountMode, setAmountMode] = useState<
    'referenceCurrency' | 'organizationCurrency' | 'both'
  >(
    DEFAULT_DELIVERY_NOTE_LAYOUT.defaultAmountCurrencyMode ??
      'referenceCurrency'
  );
  const [showLineDiscountColumn, setShowLineDiscountColumn] = useState(
    resolveDeliveryNoteShowLineDiscountColumn(DEFAULT_DELIVERY_NOTE_LAYOUT)
  );
  const [fullConfig, setFullConfig] = useState<ConfigurationFactors | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const [openTitleSection, setOpenTitleSection] = useState(false);
  const [openTypographySection, setOpenTypographySection] = useState(false);

  const sampleOrder = getSampleOrder();
  const formatNumber = (value: number) => formatExchangeRate(value);

  const refreshPreview = useCallback(() => {
    const layout: DeliveryNoteLayoutConfig = {
      blockOrder,
      blockAlignments,
      typography,
      blockFontSizes,
      blockColors,
      titleIncludesNumber,
      defaultShowAmounts: showAmounts,
      defaultAmountCurrencyMode: amountMode,
      defaultShowLineDiscountColumn: showLineDiscountColumn,
    };
    const orderForPreview =
      fullConfig?.exchangeRate != null
        ? { ...sampleOrder, exchangeRate: fullConfig.exchangeRate }
        : sampleOrder;
    const html = buildDeliveryNoteHtml(
      orderForPreview,
      {
        showAmounts,
        amountCurrencyMode: amountMode,
        showLineDiscountColumn,
      },
      layout,
      {
        logoUrl: (branding?.logo || logoUrl || '').trim(),
        logoAlt: organizationName || 'Logo',
        referenceCurrency,
        organizationCurrency,
        formatMoney,
        formatNumber,
        fiscalData: fiscalData ?? null,
      }
    );
    setPreviewHtml(html);
  }, [
    blockOrder,
    blockAlignments,
    typography,
    blockFontSizes,
    blockColors,
    titleIncludesNumber,
    showAmounts,
    amountMode,
    showLineDiscountColumn,
    fullConfig?.exchangeRate,
    branding?.logo,
    logoUrl,
    organizationName,
    referenceCurrency,
    organizationCurrency,
    formatMoney,
    fiscalData,
  ]);

  useEffect(() => {
    if (!show || !organizationId) return;
    setLoading(true);
    loadConfigurationFactors(organizationId)
      .then(config => {
        setFullConfig(config as ConfigurationFactors);
        const layoutConfig =
          config?.deliveryNoteLayout ?? DEFAULT_DELIVERY_NOTE_LAYOUT;
        const raw =
          layoutConfig && typeof layoutConfig === 'object' ? layoutConfig : {};
        setBlockOrder(
          safeBlockOrder((raw as { blockOrder?: unknown }).blockOrder)
        );
        setBlockAlignments(
          safeRecord((raw as { blockAlignments?: unknown }).blockAlignments, {})
        );
        setTypography(
          safeRecord((raw as { typography?: unknown }).typography, {})
        );
        setBlockFontSizes(
          safeRecord((raw as { blockFontSizes?: unknown }).blockFontSizes, {})
        );
        setBlockColors(
          safeRecord((raw as { blockColors?: unknown }).blockColors, {})
        );
        setTitleIncludesNumber(
          (raw as { titleIncludesNumber?: boolean }).titleIncludesNumber ?? true
        );
        setShowAmounts(
          (raw as { defaultShowAmounts?: boolean }).defaultShowAmounts ?? false
        );
        setAmountMode(
          ((raw as { defaultAmountCurrencyMode?: string })
            .defaultAmountCurrencyMode as
            | 'referenceCurrency'
            | 'organizationCurrency'
            | 'both') ?? 'referenceCurrency'
        );
        setShowLineDiscountColumn(
          resolveDeliveryNoteShowLineDiscountColumn(
            raw as DeliveryNoteLayoutConfig
          )
        );
      })
      .catch(() => {
        setBlockOrder(DEFAULT_DELIVERY_NOTE_LAYOUT.blockOrder);
      })
      .finally(() => setLoading(false));
  }, [show, organizationId]);

  useEffect(() => {
    if (show) refreshPreview();
  }, [show, refreshPreview]);

  const setBlockAlignment = (
    blockId: DeliveryNoteBlockId,
    align: DeliveryNoteBlockAlignment
  ) => {
    setBlockAlignments(prev => ({ ...prev, [blockId]: align }));
  };

  const globalTextColor =
    typography?.textColor?.trim() || DEFAULT_DELIVERY_NOTE_TEXT_COLOR;
  const setBlockFontSize = (
    blockId: DeliveryNoteBlockId,
    value: string | number
  ) => {
    if (value === '' || value === undefined) {
      setBlockFontSizes(prev => {
        const next = { ...prev };
        delete next[blockId];
        return next;
      });
    } else {
      const px =
        typeof value === 'number' ? value : parseInt(String(value), 10);
      const maxPx = blockId === 'logo' ? 500 : 72;
      if (!Number.isNaN(px) && px > 0 && px <= maxPx)
        setBlockFontSizes(prev => ({ ...prev, [blockId]: px }));
    }
  };
  const setBlockColor = (blockId: DeliveryNoteBlockId, value: string) => {
    const v = value?.trim();
    if (!v) {
      setBlockColors(prev => {
        const next = { ...prev };
        delete next[blockId];
        return next;
      });
    } else {
      setBlockColors(prev => ({ ...prev, [blockId]: v }));
    }
  };
  const clearBlockTypography = (blockId: DeliveryNoteBlockId) => {
    setBlockFontSizes(prev => {
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
    setBlockColors(prev => {
      const next = { ...prev };
      delete next[blockId];
      return next;
    });
  };

  const moveBlock = (index: number, dir: 'up' | 'down') => {
    const next = [...blockOrder];
    const j = dir === 'up' ? index - 1 : index + 1;
    if (j < 0 || j >= next.length) return;
    [next[index], next[j]] = [next[j], next[index]];
    setBlockOrder(next);
  };

  const moveBlockTo = (fromIndex: number, toIndex: number) => {
    if (fromIndex === toIndex) return;
    const next = [...blockOrder];
    const [removed] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, removed);
    setBlockOrder(next);
  };

  const removeBlock = (index: number) => {
    setBlockOrder(blockOrder.filter((_, i) => i !== index));
  };

  const addBlock = (id: DeliveryNoteBlockId) => {
    if (blockOrder.includes(id)) return;
    setBlockOrder([...blockOrder, id]);
  };

  const availableToAdd = ALL_BLOCK_IDS.filter(id => !blockOrder.includes(id));

  const handleResetToDefault = () => {
    const d = DEFAULT_DELIVERY_NOTE_LAYOUT;
    setBlockOrder([...d.blockOrder]);
    setBlockAlignments(d.blockAlignments ? { ...d.blockAlignments } : {});
    setTypography(
      d.typography ? ({ ...d.typography } as DeliveryNoteTypography) : {}
    );
    setBlockFontSizes(d.blockFontSizes ? { ...d.blockFontSizes } : {});
    setBlockColors(d.blockColors ? { ...d.blockColors } : {});
    setTitleIncludesNumber(d.titleIncludesNumber ?? true);
    setShowAmounts(d.defaultShowAmounts ?? false);
    setAmountMode(d.defaultAmountCurrencyMode ?? 'referenceCurrency');
    setShowLineDiscountColumn(
      resolveDeliveryNoteShowLineDiscountColumn(d)
    );
  };

  const handleSave = async () => {
    if (!fullConfig) {
      addToast('Cargue la configuración antes de guardar');
      return;
    }
    setSaving(true);
    try {
      await updateConfigurationFactors(organizationId, {
        ...fullConfig,
        deliveryNoteLayout: {
          blockOrder,
          blockAlignments,
          typography,
          blockFontSizes,
          blockColors,
          titleIncludesNumber,
          defaultShowAmounts: showAmounts,
          defaultAmountCurrencyMode: amountMode,
          defaultShowLineDiscountColumn: showLineDiscountColumn,
        },
      });
      addToast('Diseño de Nota de Entrega guardado');
      setFullConfig(c =>
        c
          ? {
              ...c,
              deliveryNoteLayout: {
                blockOrder,
                blockAlignments,
                typography,
                blockFontSizes,
                blockColors,
                titleIncludesNumber,
                defaultShowAmounts: showAmounts,
                defaultAmountCurrencyMode: amountMode,
                defaultShowLineDiscountColumn: showLineDiscountColumn,
              },
            }
          : c
      );
      onSaved?.();
    } catch (e) {
      console.error(e);
      addToast('Error al guardar');
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  return (
    <Modal
      show={show}
      onHide={onClose}
      fullscreen
      className="delivery-note-sandbox"
      style={{ padding: 0 }}
    >
      <Modal.Header closeButton className="py-2">
        <Modal.Title className="d-flex align-items-center gap-2">
          <Palette />
          Sandbox: Nota de Entrega
        </Modal.Title>
      </Modal.Header>
      <Modal.Body
        className="d-flex flex-column p-0"
        style={{ overflow: 'hidden' }}
      >
        <div className="d-flex flex-grow-1" style={{ minHeight: 0 }}>
          {/* Left: preview */}
          <div
            className="flex-grow-1 d-flex flex-column bg-body-secondary"
            style={{
              minWidth: 0,
              borderRight: '1px solid var(--bs-border-color)',
            }}
          >
            <div className="p-2 border-bottom bg-body d-flex align-items-center gap-2">
              <Palette size={20} />
              <strong>Vista previa</strong>
              <Form.Check
                type="switch"
                id="preview-show-amounts"
                label="Mostrar montos"
                checked={showAmounts}
                onChange={e => setShowAmounts(e.target.checked)}
                className="ms-3"
              />
              <Form.Select
                size="sm"
                style={{ width: 'auto' }}
                value={amountMode}
                onChange={e =>
                  setAmountMode(
                    e.target.value as
                      | 'referenceCurrency'
                      | 'organizationCurrency'
                      | 'both'
                  )
                }
                disabled={!showAmounts}
              >
                <option value="referenceCurrency">Solo ref.</option>
                <option value="organizationCurrency">Solo org.</option>
                <option value="both">Ambos</option>
              </Form.Select>
              <Form.Check
                type="switch"
                id="sandbox-show-discount-col"
                label="Columna Desc. %"
                title="Solo visual: ocultar cuando todos los descuentos son 0 para no confundir. Los montos siempre reflejan descuentos si los hay."
                checked={showLineDiscountColumn}
                onChange={e => setShowLineDiscountColumn(e.target.checked)}
                className="ms-2 small"
              />
            </div>
            <div
              className="flex-grow-1 p-3 d-flex flex-column"
              style={{ minHeight: 0, overflow: 'hidden' }}
            >
              {loading ? (
                <div className="text-muted">Cargando...</div>
              ) : (
                <iframe
                  title="Vista previa Nota de Entrega"
                  srcDoc={previewHtml}
                  style={{
                    flex: '1 1 0',
                    minHeight: 0,
                    width: '100%',
                    border: '1px solid var(--bs-border-color)',
                    borderRadius: 4,
                    backgroundColor: 'var(--bs-body-bg)',
                  }}
                />
              )}
            </div>
          </div>

          {/* Right: drawer - layout editor (grid / stacked) */}
          <div
            className="d-flex flex-column bg-body border-start"
            style={{ width: '360px', minWidth: '320px', maxWidth: '100%' }}
          >
            <div className="p-3 border-bottom">
              <h6 className="mb-0">Elementos de la Nota de Entrega</h6>
              <small className="text-muted">Orden y bloques visibles</small>
            </div>
            <div className="d-flex flex-column flex-grow-1 overflow-hidden p-3">
              <div className="mb-2 border-bottom">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 d-flex align-items-center gap-1 text-body text-decoration-none w-100 text-start"
                  onClick={() => setOpenTitleSection(v => !v)}
                  aria-expanded={openTitleSection}
                >
                  {openTitleSection ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                  <span className="small fw-medium">Opciones del título</span>
                </Button>
                <Collapse in={openTitleSection}>
                  <div className="py-2">
                    <Form.Check
                      type="switch"
                      id="title-includes-number"
                      label="Incluir número en el título (ej. Nota de entrega #1)"
                      checked={titleIncludesNumber}
                      onChange={e => setTitleIncludesNumber(e.target.checked)}
                      className="small"
                    />
                  </div>
                </Collapse>
              </div>
              <div className="mb-2 border-bottom">
                <Button
                  variant="link"
                  size="sm"
                  className="p-0 d-flex align-items-center gap-1 text-body text-decoration-none w-100 text-start"
                  onClick={() => setOpenTypographySection(v => !v)}
                  aria-expanded={openTypographySection}
                >
                  {openTypographySection ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                  <span className="small fw-medium">Tipografía global</span>
                </Button>
                <Collapse in={openTypographySection}>
                  <div className="py-2">
                    <div className="mb-2">
                      <Form.Label className="small mb-0">Fuente</Form.Label>
                      <Form.Select
                        size="sm"
                        value={
                          typography?.fontFamily &&
                          FONT_OPTIONS_VALUES.has(typography.fontFamily)
                            ? typography.fontFamily
                            : CUSTOM_FONT_VALUE
                        }
                        onChange={e => {
                          const v = e.target.value;
                          setTypography(prev => ({
                            ...prev,
                            fontFamily:
                              v === CUSTOM_FONT_VALUE
                                ? prev.fontFamily
                                : v || undefined,
                          }));
                        }}
                      >
                        {FONT_OPTIONS.map(opt => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                        <option value={CUSTOM_FONT_VALUE}>Otra...</option>
                      </Form.Select>
                      {(!typography?.fontFamily ||
                        !FONT_OPTIONS_VALUES.has(typography.fontFamily)) && (
                        <Form.Control
                          size="sm"
                          type="text"
                          className="mt-1"
                          value={typography?.fontFamily ?? ''}
                          onChange={e =>
                            setTypography(prev => ({
                              ...prev,
                              fontFamily: e.target.value || undefined,
                            }))
                          }
                          placeholder="ej. Arial, sans-serif"
                        />
                      )}
                    </div>
                    <div className="d-flex gap-2 align-items-end flex-wrap">
                      <div>
                        <Form.Label className="small mb-0">Color</Form.Label>
                        <div className="d-flex align-items-center gap-1">
                          <Form.Control
                            size="sm"
                            type="color"
                            style={{ width: 36, height: 28, padding: 2 }}
                            value={
                              typography?.textColor?.trim() ||
                              DEFAULT_DELIVERY_NOTE_TEXT_COLOR
                            }
                            onChange={e =>
                              setTypography(prev => ({
                                ...prev,
                                textColor: e.target.value,
                              }))
                            }
                          />
                          <Form.Control
                            size="sm"
                            type="text"
                            style={{ width: 80 }}
                            value={typography?.textColor ?? ''}
                            onChange={e =>
                              setTypography(prev => ({
                                ...prev,
                                textColor: e.target.value || undefined,
                              }))
                            }
                            placeholder="#111"
                          />
                        </div>
                      </div>
                      <div style={{ width: 70 }}>
                        <Form.Label className="small mb-0">
                          Tamaño base (px)
                        </Form.Label>
                        <Form.Control
                          size="sm"
                          type="number"
                          min={8}
                          max={24}
                          value={typography?.baseFontSizePx ?? ''}
                          onChange={e => {
                            const v = e.target.value;
                            const n = v === '' ? undefined : parseInt(v, 10);
                            setTypography(prev => ({
                              ...prev,
                              baseFontSizePx:
                                n !== undefined && !Number.isNaN(n)
                                  ? n
                                  : undefined,
                            }));
                          }}
                          placeholder={String(
                            DEFAULT_DELIVERY_NOTE_BASE_FONT_SIZE_PX
                          )}
                        />
                      </div>
                    </div>
                  </div>
                </Collapse>
              </div>
              <Alert variant="info" className="small py-1 mb-2">
                Arrastra por el asa o usa las flechas para cambiar el orden. Los
                elementos se muestran de arriba a abajo según esta lista.
              </Alert>
              <ListGroup
                variant="flush"
                className="flex-grow-1 overflow-auto mb-2"
              >
                {blockOrder.map((id, index) => (
                  <ListGroup.Item
                    key={`${id}-${index}`}
                    className="px-2 py-1"
                    data-drop-index={index}
                    onDragOver={e => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                    }}
                    onDrop={e => {
                      e.preventDefault();
                      const from = parseInt(
                        e.dataTransfer.getData('text/plain'),
                        10
                      );
                      const to = parseInt(
                        (e.currentTarget as HTMLElement).getAttribute(
                          'data-drop-index'
                        ) ?? '',
                        10
                      );
                      if (Number.isNaN(from) || Number.isNaN(to)) return;
                      moveBlockTo(from, to);
                      setDragIndex(null);
                    }}
                    style={{ opacity: dragIndex === index ? 0.5 : 1 }}
                  >
                    <div className="d-flex align-items-center gap-1 mb-0">
                      <span
                        className="d-flex align-items-center text-muted me-1"
                        draggable
                        onDragStart={e => {
                          e.dataTransfer.setData('text/plain', String(index));
                          e.dataTransfer.effectAllowed = 'move';
                          setDragIndex(index);
                        }}
                        onDragEnd={() => setDragIndex(null)}
                        style={{ cursor: 'grab' }}
                        title="Arrastrar para reordenar"
                      >
                        <GripVertical size={14} />
                      </span>
                      <span className="flex-grow-1 small text-truncate">
                        {BLOCK_LABELS[id]}
                      </span>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="p-0"
                        style={{ minWidth: 28, height: 26 }}
                        onClick={() => moveBlock(index, 'up')}
                        disabled={index === 0}
                      >
                        <ChevronUp size={14} />
                      </Button>
                      <Button
                        variant="outline-secondary"
                        size="sm"
                        className="p-0"
                        style={{ minWidth: 28, height: 26 }}
                        onClick={() => moveBlock(index, 'down')}
                        disabled={index === blockOrder.length - 1}
                      >
                        <ChevronDown size={14} />
                      </Button>
                      <Button
                        variant="outline-danger"
                        size="sm"
                        className="p-0"
                        style={{ minWidth: 28, height: 26 }}
                        onClick={() => removeBlock(index)}
                      >
                        <Trash3 size={12} />
                      </Button>
                    </div>
                    <div className="d-flex align-items-center gap-1 small">
                      <span className="text-muted">Alineación:</span>
                      {ALIGN_OPTIONS.map(opt => (
                        <Button
                          key={opt.value}
                          size="sm"
                          variant={
                            blockAlignments[id] === opt.value
                              ? 'primary'
                              : 'outline-secondary'
                          }
                          className="py-0 px-1"
                          style={{ fontSize: '0.75rem', lineHeight: 1.2 }}
                          onClick={() => setBlockAlignment(id, opt.value)}
                        >
                          {opt.label}
                        </Button>
                      ))}
                    </div>
                    <div className="d-flex align-items-center gap-2 small mt-0 flex-wrap">
                      <span className="text-muted">Tamaño:</span>
                      <Form.Control
                        size="sm"
                        type="number"
                        min={id === 'logo' ? 20 : 8}
                        max={id === 'logo' ? 500 : 72}
                        style={{ width: id === 'logo' ? 64 : 56 }}
                        value={blockFontSizes[id] ?? ''}
                        onChange={e => setBlockFontSize(id, e.target.value)}
                        placeholder={id === 'logo' ? '200×70' : 'global'}
                      />
                      <span className="text-muted">Color:</span>
                      <Form.Control
                        size="sm"
                        type="color"
                        style={{ width: 28, height: 24, padding: 2 }}
                        value={blockColors[id]?.trim() || globalTextColor}
                        onChange={e => setBlockColor(id, e.target.value)}
                        title="Color de este bloque"
                      />
                      {(blockFontSizes[id] !== undefined ||
                        blockColors[id] !== undefined) && (
                        <Button
                          variant="link"
                          size="sm"
                          className="p-0 small"
                          onClick={() => clearBlockTypography(id)}
                        >
                          Global
                        </Button>
                      )}
                    </div>
                  </ListGroup.Item>
                ))}
              </ListGroup>

              {availableToAdd.length > 0 && (
                <InputGroup size="sm" className="mb-2">
                  <Form.Select
                    value=""
                    onChange={e => {
                      const v = e.target.value as DeliveryNoteBlockId;
                      if (v) addBlock(v);
                    }}
                  >
                    <option value="">Añadir elemento...</option>
                    {availableToAdd.map(id => (
                      <option key={id} value={id}>
                        {BLOCK_LABELS[id]}
                      </option>
                    ))}
                  </Form.Select>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    className="p-1"
                    onClick={() => {
                      const first = availableToAdd[0];
                      if (first) addBlock(first);
                    }}
                  >
                    <Plus size={16} />
                  </Button>
                </InputGroup>
              )}

              <div className="mt-auto pt-2 border-top">
                <div className="d-flex gap-1 flex-wrap">
                  <Button
                    variant="primary"
                    size="sm"
                    className="flex-grow-1"
                    onClick={handleSave}
                    disabled={saving || loading}
                  >
                    <Save className="me-1" style={{ fontSize: '0.875rem' }} />
                    {saving ? 'Guardando...' : 'Guardar'}
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    className="flex-grow-1"
                    onClick={handleResetToDefault}
                    disabled={saving || loading}
                  >
                    <ArrowCounterclockwise
                      className="me-1"
                      style={{ fontSize: '0.875rem' }}
                    />
                    Restablecer
                  </Button>
                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={onClose}
                  >
                    Cerrar
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal.Body>
    </Modal>
  );
}
